if (!customElements.get('collection-filters')) {
  class CollectionFilters extends HTMLElement {
    constructor() {
      super();
      this.drawer = null;
      this.filterButton = null;
      this.gridViewButtons = [];
      this.abortController = null;
    }

    connectedCallback() {
      this.drawer = this.querySelector('theme-drawer[data-filter-drawer]');
      this.filterButton = this.querySelector('[data-filter-button]');
      this.gridViewButtons = [...this.querySelectorAll('[data-grid-view]')];

      this.initFilterDrawer();
      this.initGridViewSelector();
      this.initDelegatedEvents();

      this.onPopState = () => this.fetchAndUpdatePage(window.location.href, 'filter', { fromPopState: true });
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.removeEventListener('popstate', this.onPopState);
    }

    initDelegatedEvents() {
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-filter-input]')) {
          this.handleFilterChange(e);
        }
      });

      this.addEventListener('click', (e) => {
        const chipRemove = e.target.closest('.collection-active-filters__chip-remove');
        if (chipRemove) {
          e.preventDefault();
          this.fetchAndUpdatePage(chipRemove.href);
          return;
        }

        const clearAll = e.target.closest('.collection-active-filters__clear');
        if (clearAll) {
          e.preventDefault();
          this.fetchAndUpdatePage(clearAll.href);
          return;
        }

        const drawerClear = e.target.closest('.collection-filter-drawer__clear-btn');
        if (drawerClear) {
          e.preventDefault();
          this.fetchAndUpdatePage(drawerClear.href);
          return;
        }

        const applyBtn = e.target.closest('[data-filter-apply]');
        if (applyBtn) {
          this.drawer?.close();
          return;
        }

        const sortOption = e.target.closest('[data-sort-option]');
        if (sortOption) {
          e.preventDefault();
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sortOption.dataset.sortOption);
          this.querySelector('theme-dropdown[data-mode="listbox"]')?.close();
          this.fetchAndUpdatePage(url.toString(), 'sort');
        }
      });
    }

    initFilterDrawer() {
      if (!this.filterButton || !this.drawer) return;

      this.filterButton.addEventListener('click', () => this.drawer.open());
      this.querySelector('[data-filter-drawer-close]')?.addEventListener('click', () => this.drawer.close());
    }

    isFilterDrawerOpen() {
      return this.drawer?.hasAttribute('data-open') ?? false;
    }

    async handleFilterChange(e) {
      const input = e.target;
      const isChecked = input.checked;
      let targetUrl;

      if (isChecked) {
        targetUrl = input.dataset.filterUrl;
      } else if (input.dataset.filterType === 'price_range') {
        const url = new URL(window.location.href);
        url.searchParams.delete(input.dataset.filterParamGte);
        url.searchParams.delete(input.dataset.filterParamLte);
        targetUrl = url.toString();
      } else {
        targetUrl = input.dataset.filterRemoveUrl;
      }

      if (!targetUrl) return;

      await this.fetchAndUpdatePage(targetUrl);
    }

    async fetchAndUpdatePage(targetUrl, eventType = 'filter', { fromPopState = false } = {}) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      const drawerWasOpen = this.isFilterDrawerOpen();
      this.setLoading(true);

      try {
        const cacheKey = theme.cache.scopedKey(`cfilters:${targetUrl}`);
        let html = theme.cache.get(cacheKey);
        if (html === null) {
          const response = await fetch(targetUrl, { signal });
          html = await response.text();
          if (response.ok) theme.cache.set(cacheKey, html, theme.cache.TTL_FRESH);
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');

        this.updateDrawerContent(doc);
        this.updateActiveFilters(doc);
        this.updateProductGrid(doc);
        this.updatePagination(doc);
        this.updateSortDropdown(doc);
        this.updateApplyButtonCount(doc);
        this.updateResultsCount(doc);

        if (!fromPopState) window.history.pushState({}, '', targetUrl);

        if (drawerWasOpen) {
          this.drawer?.refreshFocus();
        }

        if (!fromPopState) this.publishFilterEvent(eventType, doc, targetUrl);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Filter/sort update error:', error);
        window.location.href = targetUrl;
      } finally {
        if (!signal.aborted) {
          this.abortController = null;
          this.setLoading(false);
        }
      }
    }

    updateDrawerContent(doc) {
      const newContent = doc.querySelector('[data-filter-drawer-content]');
      const currentContent = this.querySelector('[data-filter-drawer-content]');
      if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }
    }

    updateActiveFilters(doc) {
      const newFilters = doc.querySelector('[data-active-filters]');
      const currentFilters = this.querySelector('[data-active-filters]');

      if (newFilters) {
        if (currentFilters) {
          currentFilters.replaceWith(newFilters);
        } else {
          const bar = this.querySelector('.collection-filters__bar');
          if (bar) bar.insertAdjacentElement('afterend', newFilters);
        }
      } else if (currentFilters) {
        currentFilters.remove();
      }
    }

    getGridSelector() {
      return this.dataset.gridTarget || '#product-grid';
    }

    getPaginationSelector() {
      return this.dataset.paginationTarget || '.collection-grid__pagination';
    }

    updateProductGrid(doc) {
      const gridSelector = this.getGridSelector();
      const newGrid = doc.querySelector(gridSelector);
      const currentGrid = document.querySelector(gridSelector);
      if (!newGrid || !currentGrid) return;

      currentGrid.innerHTML = newGrid.innerHTML;

      const viewSelector = this.querySelector('[data-grid-view-selector]');
      if (!viewSelector) return;

      const savedView = localStorage.getItem('collection-grid-view');
      if (savedView) {
        document.dispatchEvent(new CustomEvent('collection-grid-view:change', {
          detail: { view: savedView }
        }));
      }
    }

    updatePagination(doc) {
      const paginationSelector = this.getPaginationSelector();
      const newPagination = doc.querySelector(paginationSelector);
      const currentPagination = document.querySelector(paginationSelector);
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
      }
    }

    updateSortDropdown(doc) {
      const newValue = doc.querySelector('[data-sort-value]');
      const currentValue = this.querySelector('[data-sort-value]');
      if (newValue && currentValue) {
        currentValue.textContent = newValue.textContent;
      }

      const newMenu = doc.querySelector('[data-sort-menu]');
      const currentMenu = this.querySelector('[data-sort-menu]');
      if (newMenu && currentMenu) {
        currentMenu.innerHTML = newMenu.innerHTML;
      }
    }

    updateApplyButtonCount(doc) {
      const newCount = doc.querySelector('[data-filter-apply-count]');
      const countEl = this.querySelector('[data-filter-apply-count]');
      if (!newCount || !countEl) return;

      countEl.textContent = newCount.textContent.trim();
    }

    updateResultsCount(doc) {
      const newCount = doc.querySelector('[data-results-count]');
      const currentCount = document.querySelector('[data-results-count]');
      if (newCount && currentCount) currentCount.innerHTML = newCount.innerHTML;
    }

    publishFilterEvent(eventType, doc, url) {
      if (!window.theme?.pubsub) return;
      const events = theme.pubsub.events;

      if (eventType === 'sort') {
        theme.pubsub.publish(events.SORT_CHANGED, { url });
        return;
      }

      const activeCount = doc.querySelectorAll('[data-active-filters] .collection-active-filters__chip').length;
      if (activeCount > 0) {
        theme.pubsub.publish(events.FILTER_APPLIED, { url, activeCount });
      } else {
        theme.pubsub.publish(events.FILTER_CLEARED, { url });
      }
    }

    setLoading(show) {
      const drawerContent = this.querySelector('[data-filter-drawer-content]');
      const grid = document.querySelector(this.getGridSelector());

      if (show) {
        if (drawerContent) {
          drawerContent.style.opacity = '0.5';
          drawerContent.style.pointerEvents = 'none';
        }

        if (grid) {
          grid.style.opacity = '0.5';
          grid.style.pointerEvents = 'none';
          grid.style.transition = 'opacity 0.2s ease';
        }

        let spinner = document.querySelector('[data-filter-spinner]');
        if (!spinner && grid) {
          spinner = document.createElement('div');
          spinner.setAttribute('data-filter-spinner', '');
          spinner.className = 'collection-filters__spinner-overlay';
          spinner.innerHTML = '<div class="collection-filters__spinner"></div>';
          grid.parentElement.style.position = 'relative';
          grid.parentElement.appendChild(spinner);
        }
      } else {
        if (drawerContent) {
          drawerContent.style.opacity = '';
          drawerContent.style.pointerEvents = '';
        }

        if (grid) {
          grid.style.opacity = '';
          grid.style.pointerEvents = '';
          grid.style.transition = '';
        }

        const spinner = document.querySelector('[data-filter-spinner]');
        if (spinner) spinner.remove();
      }
    }

    initGridViewSelector() {
      if (this.gridViewButtons.length === 0) return;

      const savedView = localStorage.getItem('collection-grid-view') || '4';
      this.setActiveGridView(savedView);
      this.applyGridView(savedView);

      this.gridViewButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const view = button.dataset.gridView;
          this.setActiveGridView(view);
          this.applyGridView(view);
          localStorage.setItem('collection-grid-view', view);
        });
      });
    }

    setActiveGridView(view) {
      this.gridViewButtons.forEach((button) => {
        button.classList.toggle('collection-grid-view__button--active', button.dataset.gridView === view);
      });
    }

    applyGridView(view) {
      if (window.theme?.pubsub) {
        theme.pubsub.publish('collection-grid-view:change', { view });
      }

      document.dispatchEvent(new CustomEvent('collection-grid-view:change', {
        detail: { view }
      }));
    }
  }

  customElements.define('collection-filters', CollectionFilters);
}

if (!customElements.get('price-range-slider')) {
  class PriceRangeSlider extends HTMLElement {
    constructor() {
      super();
      this.minInput = this.querySelector('.price-range-slider__input--min');
      this.maxInput = this.querySelector('.price-range-slider__input--max');
      this.minLabel = this.querySelector('.price-range-slider__label--min');
      this.maxLabel = this.querySelector('.price-range-slider__label--max');
      this.trackFill = this.querySelector('.price-range-slider__track-fill');

      this.debounceTimer = null;
      this.handleInputChange = this.handleInputChange.bind(this);
      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() {
      this.min = parseInt(this.dataset.min) || 0;
      this.max = parseInt(this.dataset.max) || 100;
      this.currentMin = parseInt(this.dataset.currentMin) || this.min;
      this.currentMax = parseInt(this.dataset.currentMax) || this.max;
      this.paramName = this.dataset.paramName || 'filter.p.price';
      this.currency = this.dataset.currency || '{{ amount }}';

      this.updateTrackFill();

      this.minInput?.addEventListener('input', this.handleInputChange);
      this.maxInput?.addEventListener('input', this.handleInputChange);
      this.minInput?.addEventListener('change', this.handleChange);
      this.maxInput?.addEventListener('change', this.handleChange);
    }

    disconnectedCallback() {
      this.minInput?.removeEventListener('input', this.handleInputChange);
      this.maxInput?.removeEventListener('input', this.handleInputChange);
      this.minInput?.removeEventListener('change', this.handleChange);
      this.maxInput?.removeEventListener('change', this.handleChange);

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
    }

    handleInputChange() {
      let minValue = parseInt(this.minInput.value);
      let maxValue = parseInt(this.maxInput.value);

      if (minValue > maxValue) {
        minValue = maxValue;
        this.minInput.value = minValue;
      }

      if (maxValue < minValue) {
        maxValue = minValue;
        this.maxInput.value = maxValue;
      }

      this.currentMin = minValue;
      this.currentMax = maxValue;

      this.updateLabels();
      this.updateTrackFill();
    }

    handleChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        this.applyFilter();
      }, 300);
    }

    applyFilter() {
      const url = new URL(window.location.href);
      const minValue = parseInt(this.minInput.value);
      const maxValue = parseInt(this.maxInput.value);

      if (minValue === this.min && maxValue === this.max) {
        url.searchParams.delete(`${this.paramName}.gte`);
        url.searchParams.delete(`${this.paramName}.lte`);
      } else {
        if (minValue > this.min) {
          url.searchParams.set(`${this.paramName}.gte`, (minValue / 100).toFixed(2));
        } else {
          url.searchParams.delete(`${this.paramName}.gte`);
        }

        if (maxValue < this.max) {
          url.searchParams.set(`${this.paramName}.lte`, (maxValue / 100).toFixed(2));
        } else {
          url.searchParams.delete(`${this.paramName}.lte`);
        }
      }

      const collectionFilters = this.closest('collection-filters');
      if (collectionFilters) {
        collectionFilters.fetchAndUpdatePage(url.toString());
      }
    }

    updateLabels() {
      if (this.minLabel) {
        this.minLabel.textContent = this.formatMoney(this.currentMin);
      }
      if (this.maxLabel) {
        this.maxLabel.textContent = this.formatMoney(this.currentMax);
      }
    }

    updateTrackFill() {
      if (!this.trackFill) return;

      const range = this.max - this.min;
      if (range === 0) return;
      const minPercent = ((this.currentMin - this.min) / range) * 100;
      const maxPercent = ((this.currentMax - this.min) / range) * 100;

      this.trackFill.style.left = `${minPercent}%`;
      this.trackFill.style.width = `${maxPercent - minPercent}%`;
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.floor(cents / 100);

      const amountWithComma = amount.replace('.', ',').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
      const amountNoDecimalsWithComma = amountNoDecimals.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');

      const amountWithApostrophe = amount.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1'");

      return this.currency
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{ amount_with_comma_separator }}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
        .replace('{{ amount_no_decimals_with_comma_separator }}', amountNoDecimalsWithComma)
        .replace('{{amount_with_apostrophe_separator}}', amountWithApostrophe)
        .replace('{{ amount_with_apostrophe_separator }}', amountWithApostrophe)
        .replace('{{amount}}', amount)
        .replace('{{ amount }}', amount)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{ amount_no_decimals }}', amountNoDecimals);
    }
  }

  customElements.define('price-range-slider', PriceRangeSlider);
}
